import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

/**
 * Aspect-locked cropping for logos, covers and member photos.
 *
 * Cropping happens here rather than on the server because the church sees exactly what
 * it will get, and because a 4MB photo cropped to a 200px avatar should not be a 4MB
 * upload. Logos and other transparent sources should pass `outputType="image/png"`.
 * Opaque photo crops default to JPEG.
 */

const cropHasTransparency = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): boolean => {
  try {
    const { data } = context.getImageData(0, 0, width, height);
    for (let index = 3; index < data.length; index += 4) {
      if (data[index]! < 255) return true;
    }
    return false;
  } catch {
    // If the canvas cannot be read back, keep PNG rather than flatten to JPEG black.
    return true;
  }
};

export type ImageCropperOutputType = 'auto' | 'image/png' | 'image/jpeg';

export interface ImageCropperProps {
  /** Object URL or data URL of the chosen image. */
  src: string;
  /** Width divided by height. 1 for an avatar, 16/9 for a cover. */
  aspect: number;
  /** Longest edge of the output, in pixels. */
  maxSize?: number;
  circular?: boolean;
  /** Force PNG for logos; `auto` keeps transparency when detected, otherwise JPEG. */
  outputType?: ImageCropperOutputType;
  onCropped: (blob: Blob) => void;
  onCancel: () => void;
  labels: {
    zoom: string;
    apply: string;
    cancel: string;
    instructions: string;
  };
  applying?: boolean;
  className?: string;
}

export const ImageCropper = ({
  src,
  aspect,
  maxSize = 1024,
  circular = false,
  outputType = 'auto',
  onCropped,
  onCancel,
  labels,
  applying = false,
  className,
}: ImageCropperProps) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [croppedSrc, setCroppedSrc] = useState(src);
  const dragging = useRef<{ x: number; y: number } | null>(null);

  // A new image starts framed from scratch rather than inheriting the pan and zoom of
  // the last one, which would open it half off-screen.
  if (src !== croppedSrc) {
    setCroppedSrc(src);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }

  const apply = () => {
    const image = imageRef.current;
    const frame = frameRef.current;
    if (!image || !frame) return;

    const frameRect = frame.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    // Ratio between the natural image and what is on screen, so the crop is taken at
    // full resolution rather than at the size of the preview.
    const scale = image.naturalWidth / imageRect.width;

    const sourceX = (frameRect.left - imageRect.left) * scale;
    const sourceY = (frameRect.top - imageRect.top) * scale;
    const sourceWidth = frameRect.width * scale;
    const sourceHeight = frameRect.height * scale;

    const outputWidth = Math.min(maxSize, sourceWidth);
    const outputHeight = outputWidth / aspect;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(outputWidth);
    canvas.height = Math.round(outputHeight);

    const context = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
    if (!context) return;

    // Transparent sources must stay on an alpha-capable canvas. Without an explicit
    // clear, some engines initialise the bitmap opaque black and JPEG export flattens
    // the crop to a black square.
    context.clearRect(0, 0, canvas.width, canvas.height);

    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    const emit = (blob: Blob | null) => blob && onCropped(blob);
    const usePng =
      outputType === 'image/png' ||
      (outputType === 'auto' && cropHasTransparency(context, canvas.width, canvas.height));

    if (usePng) {
      canvas.toBlob(emit, 'image/png');
      return;
    }

    canvas.toBlob(
      emit,
      'image/jpeg',
      // 0.9 rather than 1: visually identical for a photo and roughly a third of the
      // bytes, which matters on the connections many churches upload over.
      0.9,
    );
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div
        className="relative mx-auto w-full max-w-md overflow-hidden rounded-lg bg-surface-sunken"
        style={{ aspectRatio: aspect }}
        onPointerDown={(event) => {
          dragging.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!dragging.current) return;
          setOffset({
            x: event.clientX - dragging.current.x,
            y: event.clientY - dragging.current.y,
          });
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
      >
        <img
          ref={imageRef}
          src={src}
          alt=""
          draggable={false}
          className="absolute top-1/2 left-1/2 max-w-none select-none"
          style={{
            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
            width: '100%',
          }}
        />

        <div
          ref={frameRef}
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]',
            circular && 'rounded-full',
          )}
        />
      </div>

      <p className="text-center text-caption text-fg-secondary">{labels.instructions}</p>

      <div className="flex items-center gap-3">
        <label htmlFor="crop-zoom" className="text-body-sm text-fg-secondary">
          {labels.zoom}
        </label>
        <input
          id="crop-zoom"
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          className="flex-1 accent-[var(--brand-primary)]"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={applying}>
          {labels.cancel}
        </Button>
        <Button onClick={apply} loading={applying}>
          {labels.apply}
        </Button>
      </div>
    </div>
  );
};
